// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/** Typed calls onto the API (design doc §9). One function per endpoint, no logic. */

import { apiLocale, newIdempotencyKey, request } from "./client";

/** A body carrying a remark, with the language it is written in. */
function withRemarkLocale(body: Record<string, unknown>): Record<string, unknown> {
  // A locale the caller names wins: the BOM page writes in the language picked there.
  return "remark" in body ? { locale: apiLocale(), ...body } : body;
}

import type {
  AffectedArchive,
  ArchiveBatchCommit,
  ArchiveDetail,
  ArchivePreflight,
  ArchiveSearchHit,
  ArchiveVersion,
  AuditLog,
  BarcodeRule,
  Bom,
  BomDetail,
  BomItem,
  BomModules,
  BomPreflight,
  CurrentUser,
  FiledArchive,
  Installation,
  MaintenanceEvent,
  MaintenanceEventDetail,
  MaintenanceSubmissionView,
  ModuleLookup,
  ModuleType,
  Page,
  PartFate,
  PositionHistory,
  ProductType,
  ProductTypeDetail,
  ProductTypeListItem,
  Role,
  ScrapModuleOutcome,
  ScrapPartOutcome,
  SerialLookup,
  ServerDraft,
  UserAccount,
  VersionCompare,
} from "./types";

export const api = {
  me: () => request<CurrentUser>("/api/v1/me"),
  logout: () => request<void>("/auth/logout", { method: "POST" }),

  /** Accounts and their roles; administrators only (§12.4). */
  users: {
    list: () => request<UserAccount[]>("/api/v1/users"),
    setRole: (userId: string, role: Role) =>
      request<void>(`/api/v1/users/${userId}/role`, { method: "PUT", body: { role } }),
  },

  /** Materials' barcode rules — shared by every line of the material, in every BOM, and
   *  editable after the BOM is published. Anyone reads them; an administrator writes. */
  barcodeRules: {
    list: (materialCodes: string[]) =>
      request<BarcodeRule[]>("/api/v1/material-barcode-rules", {
        query: { material_codes: materialCodes.join(",") },
      }),
    create: (body: { material_code: string; pattern: string; example: string }) =>
      request<BarcodeRule>("/api/v1/material-barcode-rules", { method: "POST", body }),
    update: (id: string, body: { pattern: string; example: string }) =>
      request<BarcodeRule>(`/api/v1/material-barcode-rules/${id}`, { method: "PUT", body }),
    remove: (id: string) =>
      request<void>(`/api/v1/material-barcode-rules/${id}`, { method: "DELETE" }),
  },

  /** The module SN letters; read only (component design §5.1). */
  moduleTypes: {
    list: () => request<ModuleType[]>("/api/v1/module-types"),
  },

  productTypes: {
    list: (query: { cursor?: string; limit?: number; status?: string } = {}) =>
      request<Page<ProductTypeListItem>>("/api/v1/product-types", { query }),
    get: (id: string) => request<ProductTypeDetail>(`/api/v1/product-types/${id}`),
    create: (body: {
      name: string;
      has_bom: boolean;
      archive_prefix: string;
      device_no_regex?: string | null;
      sn_type_code?: string | null;
    }) => request<ProductType>("/api/v1/product-types", { method: "POST", body }),
    discontinue: (id: string) =>
      request<ProductType>(`/api/v1/product-types/${id}/discontinue`, { method: "POST" }),
    activate: (id: string) =>
      request<ProductType>(`/api/v1/product-types/${id}/activate`, { method: "POST" }),
    remove: (id: string) => request<void>(`/api/v1/product-types/${id}`, { method: "DELETE" }),
  },

  boms: {
    /** Every BOM when no product type is given; that type's versions when one is. */
    list: (
      productTypeId: string | undefined,
      query: {
        cursor?: string;
        limit?: number;
        status?: Bom["status"];
        is_disabled?: boolean;
      } = {},
    ) =>
      request<Page<Bom>>("/api/v1/boms", {
        query: { product_type_id: productTypeId || undefined, ...query },
      }),
    /** Remarks in `remarkLocale` when given (the BOM page's own pick), else the UI's. */
    get: (id: string, remarkLocale?: string) =>
      request<BomDetail>(`/api/v1/boms/${id}`, {
        query: { remark_locale: remarkLocale || undefined },
      }),
    create: (productTypeId: string, name?: string) =>
      request<Bom>("/api/v1/boms", {
        method: "POST",
        body: { product_type_id: productTypeId, name: name?.trim() || null },
      }),
    // A remark is written in the UI's language; the line's remarks in other languages are
    // left alone. Said explicitly rather than left to Accept-Language.
    addItem: (bomId: string, body: Record<string, unknown>) =>
      request(`/api/v1/boms/${bomId}/items`, { method: "POST", body: withRemarkLocale(body) }),
    updateItem: (bomId: string, itemId: string, body: Record<string, unknown>) =>
      request(`/api/v1/boms/${bomId}/items/${itemId}`, {
        method: "PATCH",
        body: withRemarkLocale(body),
      }),
    deleteItem: (bomId: string, itemId: string) =>
      request<void>(`/api/v1/boms/${bomId}/items/${itemId}`, { method: "DELETE" }),
    setCritical: (bomId: string, itemId: string, isCritical: boolean) =>
      request(`/api/v1/boms/${bomId}/items/${itemId}/critical`, {
        method: "PUT",
        body: { is_critical: isCritical },
      }),
    /** A line's remark in one language — on a published BOM too, unlike `updateItem`. */
    setLineRemark: (bomId: string, itemId: string, remark: string | null, locale?: string) =>
      request(`/api/v1/boms/${bomId}/items/${itemId}/remark`, {
        method: "PUT",
        body: { remark, locale: locale ?? apiLocale() },
      }),
    setSlotRemark: (
      bomId: string,
      itemId: string,
      slotNo: number,
      remark: string | null,
      locale?: string,
    ) =>
      request(`/api/v1/boms/${bomId}/items/${itemId}/slots/${slotNo}/remark`, {
        method: "PUT",
        body: { remark, locale: locale ?? apiLocale() },
      }),
    /** `name` names the version as it is published; blank clears it. `version` numbers it
     *  (a draft must be numbered to publish); omitted keeps the number the draft has. */
    publish: (bomId: string, name: string, version?: string) =>
      request<Bom>(`/api/v1/boms/${bomId}/publish`, {
        method: "POST",
        body: {
          name: name.trim() || null,
          ...(version?.trim() ? { version: version.trim() } : {}),
        },
      }),
    /** Number a draft — "1.5" — or blank to take its number away again. */
    setVersion: (bomId: string, version: string) =>
      request<Bom>(`/api/v1/boms/${bomId}`, {
        method: "PATCH",
        body: { version: version.trim() || null },
      }),
    /** A number to offer the draft; never assigned on its own. */
    suggestedVersion: (bomId: string) =>
      request<{ version: string | null }>(`/api/v1/boms/${bomId}/suggested-version`),
    /** Make an assembly a module, or (null) a plain assembly again. Drafts only. */
    setModuleType: (bomId: string, itemId: string, moduleType: string | null) =>
      request<BomItem>(`/api/v1/boms/${bomId}/items/${itemId}/module-type`, {
        method: "PUT",
        body: { module_type: moduleType },
      }),
    /** The robot and each module, with what a filing of each scans. */
    modules: (bomId: string) => request<BomModules>(`/api/v1/boms/${bomId}/modules`),
    derive: (bomId: string) => request<Bom>(`/api/v1/boms/${bomId}/derive`, { method: "POST" }),
    remove: (bomId: string) => request<void>(`/api/v1/boms/${bomId}`, { method: "DELETE" }),
    disable: (bomId: string) => request<Bom>(`/api/v1/boms/${bomId}/disable`, { method: "POST" }),
    enable: (bomId: string) => request<Bom>(`/api/v1/boms/${bomId}/enable`, { method: "POST" }),
  },

  archiveImports: {
    /** The blank batch-filing file (§8.5). Raw: it is a spreadsheet, not JSON. */
    /** With a BOM, filled in with a row per slot of one robot — or of one module of
     *  `moduleType`, for a module batch (component design §9.9). */
    template: (query: { bom_id?: string; module_type?: string } = {}) =>
      request<Response>("/api/v1/archive-imports/template", { raw: true, query }),
    /** `moduleType`: a module batch, each label of the file filed as a module of it. */
    preflight: (
      productTypeId: string,
      bomId: string | undefined,
      file: File,
      moduleType?: string,
    ) => {
      const form = new FormData();
      form.set("product_type_id", productTypeId);
      // Omitted rather than sent empty: the server reads a blank as a malformed UUID,
      // and "no default version" is the file-decides-per-row case, not an error.
      if (bomId) form.set("bom_id", bomId);
      if (moduleType) form.set("module_type", moduleType);
      form.set("file", file);
      return request<ArchivePreflight>("/api/v1/archive-imports/preflight", {
        method: "POST",
        body: form,
      });
    },
    /** The key is the caller's, held for the whole attempt: a batch filed twice because a
     *  response was lost is 500 machines filed twice (§7.7). */
    commit: (batchId: string, idempotencyKey: string) =>
      request<ArchiveBatchCommit>(`/api/v1/archive-imports/${batchId}/commit`, {
        method: "POST",
        idempotencyKey,
      }),
  },

  bomImports: {
    /** The blank import file (§8.2). Raw, because it is a spreadsheet rather than JSON. */
    template: () => request<Response>("/api/v1/bom-imports/template", { raw: true }),
    /** `sheet` reads that sheet of a workbook instead of the first one that is a BOM. */
    preflight: (productTypeId: string, file: File, sheet?: string) => {
      const form = new FormData();
      form.set("product_type_id", productTypeId);
      form.set("file", file);
      if (sheet) form.set("sheet", sheet);
      return request<BomPreflight>("/api/v1/bom-imports/preflight", {
        method: "POST",
        body: form,
      });
    },
    /** `acknowledgedWarnings`: the importer has checked the preflight's warnings. */
    commit: (batchId: string, acknowledgedWarnings = false) =>
      request<{ bom_id: string; version: string | null; row_count: number }>(
        `/api/v1/bom-imports/${batchId}/commit`,
        {
          method: "POST",
          idempotencyKey: newIdempotencyKey(),
          body: { acknowledged_warnings: acknowledgedWarnings },
        },
      ),
  },

  archives: {
    search: (
      query: {
        q?: string;
        product_type_id?: string;
        status?: string;
        source_bom_id?: string;
        kind?: "ROBOT" | "MODULE";
        module_type?: string;
        module_state?: "IN_STOCK" | "INSTALLED";
        cursor?: string;
        limit?: number;
      } = {},
    ) => request<Page<ArchiveSearchHit>>("/api/v1/archives/search", { query }),
    /** `expand` hangs each module slot's module under it, level by level; `version` gives
     *  that version's items instead of the current ones (the machine before a change). */
    get: (id: string, expand = false, version?: number) =>
      request<ArchiveDetail>(`/api/v1/archives/${id}`, {
        query: { expand: expand ? "modules" : undefined, version },
      }),
    /** A scanned module SN or module archive number, and where that module is. */
    lookupModule: (code: string) =>
      request<ModuleLookup>("/api/v1/archives/modules/lookup", { query: { code } }),
    installations: (id: string) => request<Installation[]>(`/api/v1/archives/${id}/installations`),
    positionHistory: (id: string, positionId: string) =>
      request<PositionHistory>(`/api/v1/archives/${id}/positions/${positionId}/history`),
    /** Repeated `serial=` parameters, which the generic query builder cannot express. */
    installedSerials: (serials: string[]) => {
      const params = new URLSearchParams();
      for (const serial of serials) params.append("serial", serial);
      return request<SerialLookup>(`/api/v1/archives/installed-serials?${params.toString()}`);
    },
    file: (body: Record<string, unknown>, idempotencyKey: string) =>
      request<FiledArchive>("/api/v1/archives", { method: "POST", body, idempotencyKey }),
    /** A robot's device ID, filled in after filing or corrected. */
    setDeviceNo: (id: string, deviceNo: string) =>
      request<{ id: string; device_no: string | null }>(`/api/v1/archives/${id}/device-no`, {
        method: "PUT",
        body: { device_no: deviceNo },
      }),
    /** `modules`: each fitted module's fate, by archive id — released (default) or scrapped.
     *  `parts`: each serialized part's, by position id — restocked (default) or scrapped. */
    scrap: (
      id: string,
      baseVersion: number,
      reason: string,
      modules: Record<string, "RELEASE" | "SCRAP"> = {},
      parts: Record<string, PartFate> = {},
    ) =>
      request<{
        version_no: number;
        released_serials: string[];
        modules?: ScrapModuleOutcome[];
        parts?: ScrapPartOutcome[];
      }>(`/api/v1/archives/${id}/scrap`, {
        method: "POST",
        body: { base_version: baseVersion, reason, modules, parts },
        idempotencyKey: newIdempotencyKey(),
      }),
    versions: (id: string, query: { cursor?: string; limit?: number } = {}) =>
      request<Page<ArchiveVersion>>(`/api/v1/archives/${id}/versions`, { query }),
    version: (id: string, versionNo: number, expand = false) =>
      request<{ version: ArchiveVersion; items: ArchiveDetail["items"] }>(
        `/api/v1/archives/${id}/versions/${versionNo}`,
        { query: { expand: expand ? "modules" : undefined } },
      ),
    compare: (id: string, from: number, to: number) =>
      request<VersionCompare>(`/api/v1/archives/${id}/compare`, { query: { from, to } }),
  },

  /** The signed-in user's drafts, kept on the server (§10.2). */
  drafts: {
    list: () => request<{ items: ServerDraft[] }>("/api/v1/drafts"),
    get: (workflow: string, subject: string) =>
      request<ServerDraft>(`/api/v1/drafts/${workflow}/${encodeURIComponent(subject)}`),
    save: (
      workflow: string,
      subject: string,
      body: { schema_version: number; payload: unknown; updated_at: string },
    ) =>
      request<void>(`/api/v1/drafts/${workflow}/${encodeURIComponent(subject)}`, {
        method: "PUT",
        body,
      }),
    remove: (workflow: string, subject: string) =>
      request<void>(`/api/v1/drafts/${workflow}/${encodeURIComponent(subject)}`, {
        method: "DELETE",
      }),
  },

  maintenance: {
    list: (
      archiveId: string,
      query: {
        cursor?: string;
        limit?: number;
        position_id?: string;
        action?: string;
        occurred_from?: string;
        occurred_to?: string;
        /** Only the record still in its window that brought about the current version. */
        correctable?: boolean;
      } = {},
    ) =>
      request<Page<MaintenanceEvent>>(`/api/v1/archives/${archiveId}/maintenance-events`, {
        query,
      }),
    get: (archiveId: string, eventId: string) =>
      request<MaintenanceEventDetail>(
        `/api/v1/archives/${archiveId}/maintenance-events/${eventId}`,
      ),
    submit: (archiveId: string, body: Record<string, unknown>, idempotencyKey: string) =>
      request<MaintenanceEvent & { submission_id?: string | null; affected?: AffectedArchive[] }>(
        `/api/v1/archives/${archiveId}/maintenance-events`,
        {
          method: "POST",
          body,
          idempotencyKey,
        },
      ),
    /** The whole submission an event belongs to, for correcting it (§8.6.3). */
    submission: (archiveId: string, eventId: string) =>
      request<MaintenanceSubmissionView>(
        `/api/v1/archives/${archiveId}/maintenance-events/${eventId}/submission`,
      ),
    edit: (
      archiveId: string,
      eventId: string,
      body: Record<string, unknown>,
      idempotencyKey: string,
    ) =>
      request<MaintenanceEvent & { affected?: AffectedArchive[] }>(
        `/api/v1/archives/${archiveId}/maintenance-events/${eventId}`,
        { method: "PUT", body, idempotencyKey },
      ),
    revoke: (archiveId: string, eventId: string) =>
      request<{ affected: AffectedArchive[] }>(
        `/api/v1/archives/${archiveId}/maintenance-events/${eventId}`,
        { method: "DELETE" },
      ),
  },

  audit: {
    list: (query: Record<string, string | number | undefined> = {}) =>
      request<Page<AuditLog>>("/api/v1/audit-logs", { query }),
  },

  clientPolicy: () =>
    request<{
      min_version: string | null;
      current_locale: string;
      server_time: string;
      environment: string;
      labels_printable: boolean;
    }>("/api/v1/system/client-policy"),
};

/**
 * CSV exports are downloads, so they are links rather than fetches — and a followed link
 * sends the browser's Accept-Language, not the language chosen here. Each one therefore
 * names the app's language itself (`lang`), so the headers match the screen.
 */
export const exportUrls = {
  archives: (query: Record<string, string | undefined>) =>
    withQuery("/api/v1/archives/export", { ...query, lang: apiLocale() }),
  version: (archiveId: string, versionNo: number) =>
    withQuery(`/api/v1/archives/${archiveId}/versions/${versionNo}/export`, {
      lang: apiLocale(),
    }),
  maintenance: (archiveId: string, query: Record<string, string | undefined> = {}) =>
    withQuery(`/api/v1/archives/${archiveId}/maintenance-events/export`, {
      ...query,
      lang: apiLocale(),
    }),
  audit: (query: Record<string, string | undefined> = {}) =>
    withQuery("/api/v1/audit-logs/export", { ...query, lang: apiLocale() }),
};

function withQuery(path: string, query: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}
